import React, { FC } from 'react'
import {
  Link,
  ConfirmationDialog,
  ModalInjectedProps,
} from '@sketch/components'

import { useToast } from '@sketch/toasts'
import { deleteEntityFromCache, routes } from '@sketch/modules-common'

import useGetHasDraftsProject from 'modules/projects/operations/useGetHasDraftsProject'
import { useLeaveWorkspace } from 'modules/user/operations'

interface LeaveWorkspaceModalProps extends ModalInjectedProps {
  workspace: { identifier: string; name: string }
  membershipId: string
}

/**
 * LeaveWorkspaceModal
 *
 * This component is visually a Modal that Prompts the user
 * with a confirmation to leave the workspace
 */
const LeaveWorkspaceModal: FC<LeaveWorkspaceModalProps> = props => {
  const { hideModal, isModalOpen, workspace, membershipId } = props

  const { showToast } = useToast()

  const [leaveWorkspace, { loading }] = useLeaveWorkspace({
    variables: { membershipId },
    onError: message => showToast(message, 'negative'),
    onCompleted: () => {
      showToast(`You have been removed from "${workspace.name}"`)
      hideModal()
    },
    update: cache => {
      /* Remove all the workspace information from the cache */
      deleteEntityFromCache(cache, {
        __typename: 'Workspace',
        identifier: workspace.identifier,
      })
    },
  })

  const hasDraftsProject = useGetHasDraftsProject(workspace.identifier)

  return (
    <ConfirmationDialog
      title="Leave the Workspace?"
      hideModal={hideModal}
      isModalOpen={isModalOpen}
      onConfirm={leaveWorkspace}
      confirmButton={{
        text: 'Leave',
        loading,
        disabled: loading,
        variant: 'negative-secondary',
      }}
    >
      <p>
        After you leave you won&apos;t be able to view, comment on, or upload
        documents to &quot;<strong>{workspace.name}</strong>&quot; anymore.
      </p>
      {hasDraftsProject && (
        <p>
          Make sure you back up any private work in
          <Link
            to={routes.WORKSPACE_DRAFTS.create({
              workspaceId: workspace.identifier,
            })}
            onClick={() => hideModal()}
          >
            {' '}
            My Drafts{' '}
          </Link>
          before you leave.
        </p>
      )}
    </ConfirmationDialog>
  )
}

export default LeaveWorkspaceModal
